import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _excluded = ["title", "as", "pageType", "slug", "url"];
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
import React, { useRef, useEffect, useContext } from 'react';
import { Dropdown } from 'semantic-ui-react';
import _get from 'lodash/get';
import { v4 as uuidv4 } from 'uuid';
import { ANGLE_DOWN_ICON, CHEVRON_RIGHT_ICON, SLIDE_ANIMATION_DURATION } from './DropdownMenu.constants';
import BaseLink from '../../../../../../atoms/Link';
import useResponsive from '../../../../../../hooks/use-responsive/use-responsive';
import { isExternalPage } from '../../../../../../services/pages';
import { AsPropType, SiteNavigationLinkPropType } from '../../../../../../services/proptypes';
import Event, { EVENT_CATEGORIES } from '../../../../../analytics/components/Event';
import { NAVIGATION_MENU_CLICK_EVENT_ACTION } from '../../../../../analytics/components/Event/Event.constants';
import { HEIGHT_TRANSITION } from '../../../../../animations/animation.constants';
import useFixedValueAnimation from '../../../../../animations/hooks/use-fixed-value-animation';
import FishermanIcon from '../../../../../foundation/components/Icon';
import DimensionUtils from '../../../../../foundation/services/dimensions';
import { cleanLinkProps, sortLinks } from '../../../../services/links';
import { NavigationContext } from '../../Navigation.context';
import { DEFAULT_LINK_AS } from '../Link/Link.constants';
import { AS_BUTTON } from '../Links/Links.constants';
import './DropdownMenu.less';
var defaultProps = {
  linksAs: DEFAULT_LINK_AS,
  className: ''
};
function DropdownMenu(_ref) {
  var title = _ref.title,
    options = _ref.options,
    linksAs = _ref.linksAs,
    className = _ref.className;
  var uuid = useRef(uuidv4());
  var _useContext = useContext(NavigationContext),
    toggled = _useContext.toggled,
    openedDropdownMenus = _useContext.openedDropdownMenus,
    setOpenedDropdownMenus = _useContext.setOpenedDropdownMenus,
    setOpenedAccordionPopups = _useContext.setOpenedAccordionPopups;
  var dropdownTriggerRef = useRef(null);
  var dropdownRef = useRef(null);
  var contentRef = useRef(null);
  var open = openedDropdownMenus.includes(uuid.current);
  var sortedOptions = sortLinks(options);
  var cleanedOptions = cleanLinkProps(sortedOptions);
  var _useResponsive = useResponsive(),
    isDesktop = _useResponsive.isDesktop,
    isMobile = _useResponsive.isMobile,
    isTablet = _useResponsive.isTablet,
    isTouchScreenDevice = _useResponsive.isTouchScreenDevice;
  var height = open ? 'auto' : 0;
  var _useFixedValueAnimati = useFixedValueAnimation(height, contentRef, HEIGHT_TRANSITION, {
      duration: SLIDE_ANIMATION_DURATION
    }),
    currentHeight = _useFixedValueAnimati.currentValue,
    overflow = _useFixedValueAnimati.overflow;
  var _DimensionUtils$getRe = DimensionUtils.getRelativeDistanceToWindowBorders(dropdownTriggerRef, {
      thresholdPercentage: 0.2
    }),
    tooCloseToRight = _DimensionUtils$getRe.tooCloseToRight;
  function showMenu() {
    setOpenedDropdownMenus([uuid.current]);
    setOpenedAccordionPopups([]);
  }
  function hideMenu() {
    setOpenedDropdownMenus(function (previousOpened) {
      return previousOpened.filter(function (menuUuid) {
        return menuUuid !== uuid.current;
      });
    });
  }
  function onClick(event) {
    var _ref2 = event || {},
      _ref2$target = _ref2.target,
      target = _ref2$target === void 0 ? {} : _ref2$target;
    if (dropdownRef.current) {
      // dispatches click event as semantic ui will stop propagation of the native one
      window.dispatchEvent(new MouseEvent('click', {
        bubbles: true,
        target: target
      }));
    }
  }
  function onHover() {
    if (isTouchScreenDevice) {
      return;
    }
    showMenu();
  }
  useEffect(function () {
    if (!toggled) {
      // make sure the popup is closed when the tray is closed
      hideMenu();
    }
  }, [toggled]);
  var asButton = _get(cleanedOptions, '[0].as') === AS_BUTTON;
  var iconName = isMobile || isTablet ? CHEVRON_RIGHT_ICON : ANGLE_DOWN_ICON;
  if (open) {
    iconName = 'xmark';
  }
  var contentStyle = {
    transition: "height ".concat(SLIDE_ANIMATION_DURATION, "ms ease"),
    height: currentHeight,
    overflow: overflow
  };
  if (isDesktop && tooCloseToRight) {
    contentStyle = _objectSpread(_objectSpread({}, contentStyle), {}, {
      left: 'auto',
      right: 0
    });
  }
  function getTrigger() {
    if (asButton) {
      return /*#__PURE__*/React.createElement("div", {
        ref: dropdownTriggerRef,
        className: "ui icon primary right labeled button text divider ".concat(open ? 'active' : '')
      }, /*#__PURE__*/React.createElement("span", null, title), /*#__PURE__*/React.createElement(FishermanIcon, {
        iconName: iconName
      }));
    }
    return /*#__PURE__*/React.createElement("div", {
      ref: dropdownTriggerRef,
      className: "divider text"
    }, title, asButton && /*#__PURE__*/React.createElement(FishermanIcon, {
      iconName: iconName
    }));
  }
  return /*#__PURE__*/React.createElement(Dropdown, {
    floating: !!isMobile,
    item: true,
    text: getTrigger(),
    className: "dropdown-menu",
    icon: asButton ? null : /*#__PURE__*/React.createElement(FishermanIcon, {
      iconName: iconName
    }),
    "aria-label": "dropdown-menu-links",
    ref: dropdownRef,
    onClick: onClick,
    open: open,
    onOpen: showMenu,
    onClose: hideMenu,
    button: asButton,
    onMouseEnter: onHover
  }, /*#__PURE__*/React.createElement(Dropdown.Menu, {
    open: open,
    style: contentStyle
  }, /*#__PURE__*/React.createElement("div", {
    ref: contentRef
  }, cleanedOptions.map(function (_ref3) {
    var linkTitle = _ref3.title,
      as = _ref3.as,
      pageType = _ref3.pageType,
      slug = _ref3.slug,
      url = _ref3.url,
      rest = _objectWithoutProperties(_ref3, _excluded);
    return /*#__PURE__*/React.createElement(Event, {
      key: linkTitle,
      event: {
        category: EVENT_CATEGORIES.NAVIGATION_EVENT_CATEGORY,
        action: NAVIGATION_MENU_CLICK_EVENT_ACTION,
        label: linkTitle
      },
      eventType: "click"
    }, /*#__PURE__*/React.createElement(Dropdown.Item, _extends({
      key: linkTitle,
      as: BaseLink,
      to: isExternalPage(pageType) ? url : slug,
      linksAs: as || linksAs,
      title: linkTitle,
      className: className,
      internal: !isExternalPage(pageType),
      role: "option"
    }, rest)));
  }))));
}
DropdownMenu.defaultProps = defaultProps;
export default DropdownMenu;